import { useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import dayjs, { Dayjs } from 'dayjs';
import { TableFilter, useTableFilterContext } from 'features/TableFilter';
import { FC, memo, useCallback } from 'react';
import { DatePicker } from 'shared/ui/DatePicker';
import { InputNumber } from 'shared/ui/InputNumber';
import { RangeDatePicker } from 'shared/ui/RangeDatePicker/RangeDatePicker';
import { RangeInput } from 'shared/ui/RangeInput/RangeInput';
import { CLIENT_DATE_FORMAT } from 'shared/utils/constants';
import { InvoicesListFilterKeys } from '../../model/types';
import { AccountTypeSelect } from './AccountTypeSelect';
import { ApplicabilitySelect } from './ApplicabilitySelect';
import { ContractStatusSelect } from './ContractStatusSelect';
import { InvoiceStatusSelect } from './InvoiceStatusSelect';
import { InvoiceTypeSelect } from './InvoiceTypeSelect';
import { PaymentMethodSelect } from './PaymentMethodSelect';
import { UsersSelect } from './UsersSelect';
import { WarehouseSelect } from './WarehouseSelect';
import { Checkbox } from 'shared/ui/Checkbox';
import { Input } from 'shared/ui/Input';

export const InvoicesListFilter: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'common']);

  const currencySymbol = useGetCurrencySymbol();

  const { filters, changeFilters, clearCurrentFilters } = useTableFilterContext<InvoicesListFilterKeys>();

  const selectWarehouse = useCallback(
    (value: string[]) => {
      changeFilters({
        key: InvoicesListFilterKeys.selectedWarehouses,
        label: `${t('Selected')}: ${value.length}`,
        value,
        title: t('Warehouse'),
      });
    },
    [changeFilters, t],
  );

  const changeInvoicingDateFrom = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateFrom = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: InvoicesListFilterKeys.invoicingDateFrom,
        label: t('From {{invoicingDateFrom}}', { invoicingDateFrom: clientFormatDateFrom }),
        value,
        title: t('Invoicing date'),
      });
    },
    [changeFilters, t],
  );

  const changeInvoicingDateTo = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateTo = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: InvoicesListFilterKeys.invoicingDateTo,
        label: t('To {{invoicingDateTo}}', { invoicingDateTo: clientFormatDateTo }),
        value,
        title: t('Invoicing date'),
      });
    },
    [changeFilters, t],
  );

  const changeCreationDateFrom = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateFrom = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: InvoicesListFilterKeys.creationDateFrom,
        label: t('From {{creationDateFrom}}', { creationDateFrom: clientFormatDateFrom }),
        value,
        title: t('Creation date'),
      });
    },
    [changeFilters, t],
  );

  const changeCreationDateTo = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateTo = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: InvoicesListFilterKeys.creationDateTo,
        label: t('To {{creationDateTo}}', { creationDateTo: clientFormatDateTo }),
        value,
        title: t('Creation date'),
      });
    },
    [changeFilters, t],
  );

  const changeDueDateFrom = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateFrom = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: InvoicesListFilterKeys.dueDateFrom,
        label: t('From {{dueDateFrom}}', { dueDateFrom: clientFormatDateFrom }),
        value,
        title: t('Due date'),
      });
    },
    [changeFilters, t],
  );

  const changeDueDateTo = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateTo = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: InvoicesListFilterKeys.dueDateTo,
        label: t('To {{dueDateTo}}', { dueDateTo: clientFormatDateTo }),
        value,
        title: t('Due date'),
      });
    },
    [changeFilters, t],
  );

  const changeInvoiceStatus = useCallback(
    (value: string, label: string) => {
      changeFilters({ key: InvoicesListFilterKeys.status, label, value, title: t('Status') });
    },
    [changeFilters, t],
  );

  const changeInvoiceType = useCallback(
    (value: string[]) => {
      changeFilters({
        key: InvoicesListFilterKeys.invoiceTypes,
        label: `${t('Selected')}: ${value.length}`,
        value,
        title: t('Invoice type'),
      });
    },
    [changeFilters, t],
  );

  const changeInvoiceAmount = useCallback(
    (value: Nullable<number>) => {
      changeFilters({ key: InvoicesListFilterKeys.invoiceAmount, label: `${value} ${currencySymbol}`, value, title: t('Total amount') });
    },
    [currencySymbol, changeFilters, t],
  );

  const changeInvoiceNumberFrom = useCallback(
    (value: Nullable<string>) => {
      changeFilters({
        key: InvoicesListFilterKeys.invoiceNumberFrom,
        label: t('From {{invoiceNumberFrom}}', { invoiceNumberFrom: value }),
        value,
        title: t('Invoice number'),
      });
    },
    [changeFilters, t],
  );

  const changeInvoiceNumberTo = useCallback(
    (value: Nullable<string>) => {
      changeFilters({
        key: InvoicesListFilterKeys.invoiceNumberTo,
        label: t('To {{invoiceNumberTo}}', { invoiceNumberTo: value }),
        value,
        title: t('Invoice number'),
      });
    },
    [changeFilters, t],
  );

  const changeStatusRefDate = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatStatusRefDate = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: InvoicesListFilterKeys.statusRefDate,
        label: clientFormatStatusRefDate,
        value,
        title: t('statusRefDate'),
      });
    },
    [changeFilters, t],
  );

  const changeContractApplicability = useCallback(
    (value: string, label: string) => {
      changeFilters({ key: InvoicesListFilterKeys.isApplicable, label, value, title: t('Applicability') });
    },
    [changeFilters, t],
  );

  const changeContractStatus = useCallback(
    (value: string, label: string) => {
      changeFilters({ key: InvoicesListFilterKeys.contractStatus, label, value, title: t('Contract status') });
    },
    [changeFilters, t],
  );

  const changePaymentMethod = useCallback(
    (value: string[]) => {
      changeFilters({
        key: InvoicesListFilterKeys.paymentMethods,
        label: `${t('Selected')}: ${value.length}`,
        value,
        title: t('Payment method'),
      });
    },
    [changeFilters, t],
  );

  const changeAccountType = useCallback(
    (value: string, label: string) => {
      changeFilters({ key: InvoicesListFilterKeys.accountType, label, value, title: t('Account type') });
    },
    [changeFilters, t],
  );

  const selectUsers = useCallback(
    (value: string[]) => {
      changeFilters({
        key: InvoicesListFilterKeys.userIds,
        label: `${t('Selected')}: ${value.length}`,
        value,
        title: t('Customer'),
      });
    },
    [changeFilters, t],
  );

  const changeDiscountKey = useCallback(
    (value: Nullable<string>) => {
      changeFilters({
        key: InvoicesListFilterKeys.discountKey,
        label: `${value}`,
        value: value || undefined,
        title: t('Discount'),
      });
    },
    [changeFilters, t],
  );

  const changePaymentDateFrom = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateFrom = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: InvoicesListFilterKeys.paymentDateFrom,
        label: t('From {{invoicingDateFrom}}', { invoicingDateFrom: clientFormatDateFrom }),
        value,
        title: t('Payment date'),
      });
    },
    [changeFilters, t],
  );

  const changeHasCreditNotes = useCallback(
    (value: boolean) => {
      value
        ? changeFilters({
            key: InvoicesListFilterKeys.hasCreditNotes,
            label: t('Invoices with credit notes'),
            value,
            title: t('Invoices with credit notes'),
          })
        : clearCurrentFilters(InvoicesListFilterKeys.hasCreditNotes);
    },
    [changeFilters, clearCurrentFilters, t],
  );

  const changePaymentDateTo = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateTo = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: InvoicesListFilterKeys.paymentDateTo,
        label: t('To {{invoicingDateTo}}', { invoicingDateTo: clientFormatDateTo }),
        value,
        title: t('Payment date'),
      });
    },
    [changeFilters, t],
  );

  return (
    <TableFilter>
      <div className="space-y-2">
        <WarehouseSelect values={filters.selectedWarehouses?.value} onChange={selectWarehouse} onClear={clearCurrentFilters} />
        <RangeDatePicker
          placeholder={[t('From'), t('To')]}
          label={t('Invoicing range')}
          tooltip={t('Shows all invoices that satisfy selected Date From range')}
          onChangeFrom={changeInvoicingDateFrom}
          onChangeTo={changeInvoicingDateTo}
          values={[
            filters.invoicingDateFrom?.value && dayjs(filters.invoicingDateFrom?.value),
            filters.invoicingDateTo?.value && dayjs(filters.invoicingDateTo?.value),
          ]}
        />

        <RangeDatePicker
          placeholder={[t('From'), t('To')]}
          label={t('Creation range')}
          onChangeFrom={changeCreationDateFrom}
          onChangeTo={changeCreationDateTo}
          values={[
            filters.creationDateFrom?.value && dayjs(filters.creationDateFrom?.value),
            filters.creationDateTo?.value && dayjs(filters.creationDateTo?.value),
          ]}
        />

        <RangeDatePicker
          placeholder={[t('From'), t('To')]}
          label={t('Due date range')}
          onChangeFrom={changeDueDateFrom}
          onChangeTo={changeDueDateTo}
          values={[
            filters.dueDateFrom?.value && dayjs(filters.dueDateFrom?.value),
            filters.dueDateTo?.value && dayjs(filters.dueDateTo?.value),
          ]}
        />

        <RangeDatePicker
          placeholder={[t('From'), t('To')]}
          label={t('Payment date range')}
          onChangeFrom={changePaymentDateFrom}
          onChangeTo={changePaymentDateTo}
          values={[
            filters.paymentDateFrom?.value && dayjs(filters.paymentDateFrom?.value),
            filters.paymentDateTo?.value && dayjs(filters.paymentDateTo?.value),
          ]}
        />

        <RangeInput
          type="string"
          values={[filters.invoiceNumberFrom?.value, filters.invoiceNumberTo?.value]}
          placeholder={[t('From'), t('To')]}
          label={t('Invoice number')}
          onChangeFrom={changeInvoiceNumberFrom}
          onChangeTo={changeInvoiceNumberTo}
        />

        <div className="flex space-x-3 items-center">
          <div className="flex-1">
            <div className="mb-2">{t('Total amount')}</div>
            <InputNumber placeholder={t('Total amount')} value={filters.invoiceAmount?.value} onChange={changeInvoiceAmount} bordered />
          </div>
          <AccountTypeSelect value={filters.accountType?.value} onChange={changeAccountType} onClear={clearCurrentFilters} />
        </div>

        <div className="flex space-x-3 items-center">
          <div className="flex-1">
            <div className="mb-2">{t('Status date')}</div>
            <DatePicker
              className="h-[52px] w-full"
              value={filters.statusRefDate?.value && dayjs(filters.statusRefDate?.value)}
              format={CLIENT_DATE_FORMAT}
              placeholder="statusRefDate"
              onChange={changeStatusRefDate}
            />
          </div>

          <InvoiceStatusSelect value={filters.isActive?.value} onChange={changeInvoiceStatus} onClear={clearCurrentFilters} />
        </div>

        <InvoiceTypeSelect value={filters.invoiceTypes?.value} onChange={changeInvoiceType} onClear={clearCurrentFilters} />

        <PaymentMethodSelect value={filters.paymentMethods?.value} onChange={changePaymentMethod} onClear={clearCurrentFilters} />

        <UsersSelect value={filters.userIds?.value} onChange={selectUsers} onClear={clearCurrentFilters} />
        <div className="flex space-x-3 items-center">
          <ApplicabilitySelect value={filters.isApplicable?.value} onChange={changeContractApplicability} onClear={clearCurrentFilters} />

          <ContractStatusSelect value={filters.contractStatus?.value} onChange={changeContractStatus} onClear={clearCurrentFilters} />
        </div>
        <div>
          <div className="mb-2">{t('Discount key')}</div>

          <Input placeholder={t('Discount key')} value={filters.discountKey?.value} onChange={changeDiscountKey} bordered uppercase />
        </div>

        <Checkbox checked={filters.hasCreditNotes?.value || false} onChange={changeHasCreditNotes} name="hasCreditNotes" className="my-2">
          {t('Invoices with credit notes')}
        </Checkbox>
      </div>
    </TableFilter>
  );
});
